/**
 * Date utility functions for consistent, timezone-neutral date handling
 * Prevents the common issue where dates shift by +1 day due to timezone conversion
 */

/**
 * Format travel date for display with timezone-neutral handling
 * @param {string|Date} dateInput - Date in YYYY-MM-DD format or Date object
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string in YYYY-MM-DD format
 */
export const formatTravelDate = (dateInput, options = {}) => {
  if (!dateInput) return '';
  
  try {
    let date;
    
    // Handle Date objects from database (use UTC to avoid timezone conversion)
    if (dateInput instanceof Date) {
      // For Date objects from MySQL DATE fields, use UTC methods to avoid timezone conversion
      const year = dateInput.getUTCFullYear();
      const month = String(dateInput.getUTCMonth() + 1).padStart(2, '0');
      const day = String(dateInput.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } else if (typeof dateInput === 'string') {
      // Handle YYYY-MM-DD string format (already correct, no conversion needed)
      if (dateInput.includes('-') && dateInput.length === 10) {
        return dateInput; // Return as-is, no need to parse and reformat
      } else {
        // Try parsing as general date string
        const date = new Date(dateInput);
        if (!isNaN(date.getTime())) {
          const year = date.getUTCFullYear();
          const month = String(date.getUTCMonth() + 1).padStart(2, '0');
          const day = String(date.getUTCDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      }
    }
    
    // Fallback for invalid input
    console.warn('Invalid date:', dateInput);
    return String(dateInput);
  } catch (error) {
    console.warn('Error formatting date:', dateInput, error);
    return String(dateInput); // Return original if parsing fails
  }
};

/**
 * Format a date range for display
 * @param {string|Date} startDate - Start date in YYYY-MM-DD format or Date object
 * @param {string|Date} endDate - End date in YYYY-MM-DD format or Date object
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date range string
 */
export const formatDateRange = (startDate, endDate, options = {}) => {
  if (!startDate && !endDate) return '';
  
  const separator = options.separator || ' - ';
  
  if (!startDate) return formatTravelDate(endDate, options);
  if (!endDate) return formatTravelDate(startDate, options);
  
  return `${formatTravelDate(startDate, options)}${separator}${formatTravelDate(endDate, options)}`;
};

/**
 * Check if a date string is valid
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {boolean} True if valid date
 */
export const isValidTravelDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return false;
  
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateString)) return false;
  
  try {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    
    return date.getFullYear() === year &&
           date.getMonth() === month - 1 &&
           date.getDate() === day;
  } catch {
    return false;
  }
};

/**
 * Convert a Date object to YYYY-MM-DD format for database storage
 * @param {Date} date - JavaScript Date object
 * @returns {string} Date in YYYY-MM-DD format
 */
export const toTravelDateString = (date) => {
  if (!date || !(date instanceof Date)) return '';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Parse a travel date string to a Date object (timezone-neutral)
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {Date|null} Date object or null if invalid
 */
export const parseTravelDate = (dateString) => {
  if (!isValidTravelDate(dateString)) return null;
  
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Format travel date for display in MM/DD/YYYY format (timezone-neutral)
 * @param {string|Date} dateInput - Date in YYYY-MM-DD format or Date object
 * @returns {string} Formatted date string in MM/DD/YYYY format
 * Updated to fix timezone display issues
 */
export const formatTravelDateDisplay = (dateInput) => {
  if (!dateInput) return '';
  
  try {
    let date;
    
    // Handle Date objects from database (these come as UTC midnight and need timezone-neutral handling)
    if (dateInput instanceof Date) {
      // For Date objects from MySQL DATE fields, use UTC methods to avoid timezone conversion
      const year = dateInput.getUTCFullYear();
      const month = String(dateInput.getUTCMonth() + 1).padStart(2, '0');
      const day = String(dateInput.getUTCDate()).padStart(2, '0');
      return `${month}/${day}/${year}`;
    } else if (typeof dateInput === 'string') {
      // Handle YYYY-MM-DD string format
      if (dateInput.includes('-') && dateInput.length === 10) {
        const [year, month, day] = dateInput.split('-').map(Number);
        return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
      } else {
        // Try parsing as general date string
        date = new Date(dateInput);
        if (!isNaN(date.getTime())) {
          const year = date.getUTCFullYear();
          const month = String(date.getUTCMonth() + 1).padStart(2, '0');
          const day = String(date.getUTCDate()).padStart(2, '0');
          return `${month}/${day}/${year}`;
        }
      }
    }
    
    return String(dateInput);
  } catch (error) {
    console.warn('Error formatting date:', dateInput, error);
    return String(dateInput); // Return original if parsing fails
  }
};

/**
 * Parse a date-time returned by the API. MySQL DATETIME/TIMESTAMP values arrive as
 * "YYYY-MM-DD HH:MM:SS" with no zone but are stored in UTC, so they must be read as UTC
 * (the browser would otherwise treat them as local time and show the wrong hour).
 * @param {string|Date} value
 * @returns {Date|null}
 */
export const parseServerDateTime = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const text = String(value).trim();
  const naive = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(text);
  const date = new Date(naive ? `${text.replace(' ', 'T')}Z` : text);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Short name of the device's time zone (for example "CST") at a given moment, so DST is respected.
 * @param {Date} date
 * @returns {string}
 */
export const getTimeZoneAbbr = (date = new Date()) => {
  try {
    const when = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
    return new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(when).find((part) => part.type === 'timeZoneName')?.value || '';
  } catch (error) {
    return '';
  }
};
